import  { useState } from 'react';
import { motion } from 'framer-motion';
import { Product } from '../types';
import { Eye, ShoppingBag, X } from 'lucide-react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { useCart } from '../context/CartContext';

interface ProductCardProps {
  product: Product;
  index: number;
}

const AnimatedProductCard = ({ product, index }: ProductCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [quickView, setQuickView] = useState(false);
  const [ref, isInView] = useIntersectionObserver<HTMLDivElement>({ threshold: 0.2, once: true });
  const { addToCart } = useCart();
  
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleAddToCart = () => {
    addToCart(product);
  };

  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1,
        ease: "easeOut" 
      }}
      className="product-card group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative overflow-hidden mb-4">
        <motion.img 
          src={product.images[0]} 
          alt={product.name}
          className="product-image w-full h-80 object-cover"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.5 }}
        />
        
        {(product.new || product.bestSeller) && (
          <div className="absolute top-2 left-2">
            {product.new && (
              <motion.span 
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="inline-block bg-white px-3 py-1 text-xs font-medium uppercase tracking-wider shadow-sm"
              >
                New
              </motion.span>
            )}
            {product.bestSeller && (
              <motion.span 
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="inline-block bg-gold text-white px-3 py-1 text-xs font-medium uppercase tracking-wider shadow-sm ml-1"
              >
                Best Seller
              </motion.span>
            )}
          </div>
        )}
        
        <motion.div 
          className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.button 
            className="bg-white p-3 rounded-full shadow-md hover:bg-gold hover:text-white transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setQuickView(true)}
          >
            <Eye size={20} />
          </motion.button>
          <motion.button 
            className="bg-white p-3 rounded-full shadow-md hover:bg-gold hover:text-white transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAddToCart}
          >
            <ShoppingBag size={20} />
          </motion.button>
        </motion.div>
      </div>
      
      <motion.div 
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
      >
        <h3 className="font-serif text-lg mb-1">{product.name}</h3>
        <p className="text-gold">{formatPrice(product.price)}</p>
      </motion.div>

      {/* Quick View Modal */}
      {quickView && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <motion.div 
            className="bg-white w-full md:max-w-4xl max-w-[90vw] max-h-[90vh] rounded-lg overflow-hidden relative"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <button 
              className="absolute top-4 right-4 z-10 bg-white/10 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/20 transition-colors"
              onClick={() => setQuickView(false)}
            >
              <X size={24} />
            </button>
            <div className="p-4 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="aspect-square md:h-96 overflow-hidden rounded-lg">
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <h3 className="font-serif text-2xl mb-2">{product.name}</h3>
                  <p className="text-gold text-xl mb-4">${product.price.toLocaleString()}</p>
                  <p className="text-gray-600 mb-6">{product.description}</p>
                  <button
                    onClick={handleAddToCart}
                    className="w-full bg-gold text-white py-3 rounded-lg hover:bg-gold/90 transition-colors"
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default AnimatedProductCard;
 